// Demo portfolio — the fixture behind the "in development" preview dashboard.
//
// This is a STATIC fixture transcribed from backend/scripts/demo_data/ledger.yaml.
// It never touches the network or the in-development ledger pipeline. Every number
// here ties back to a demo PDF the prospect can already see in the Documents tab.
//
// The preview reuses the LIVE dashboard surface (portal tabs → fund → investor →
// document timeline). To do that, this module reshapes the fixture into the exact
// shapes the live components consume:
//   buildOverview(portfolio)  → { portals: [{ portal, funds: [{ ..., members:[...] }] }] }
//   buildDocTimeline(fund)    → { documents: [{ ...position_after, deltas, contributions }] }
// matching routes/portfolio.py::/v3/ledger/overview and /document-timeline.
//
// One synthetic event is seeded: a duplicate Apex distribution (flagged synthetic)
// so a prospect can try "Remove" on an obviously-duplicated document.

// ─── The fixture ────────────────────────────────────────────────────────────────

export const DEMO_PORTFOLIO = {
  investor: "Sandbox Holdings LP",
  funds: [
    {
      id: "fnd_apex_meridian_growth_iii",
      portal: "Apex",
      fundName: "Meridian Growth Fund III",
      currency: "EUR",
      events: [
        { id: "apex-sub", date: "2023-04-12", type: "commitment", docType: "Subscription Confirmation", amount: 5000000, source_pdf: "apex_meridian_2023-04-12_subscription.pdf" },
        { id: "apex-cc1", date: "2023-09-20", type: "capital_call", docType: "Capital Call #1", amount: 1000000, source_pdf: "apex_meridian_2023-09-20_cc1.pdf" },
        { id: "apex-cc2", date: "2024-04-15", type: "capital_call", docType: "Capital Call #2", amount: 1000000, source_pdf: "apex_meridian_2024-04-15_cc2.pdf" },
        { id: "apex-cc3", date: "2025-02-10", type: "capital_call", docType: "Capital Call #3", amount: 1000000, source_pdf: "apex_meridian_2025-02-10_cc3.pdf" },
        { id: "apex-dist1", date: "2025-03-22", type: "distribution", docType: "Distribution #1", amount: 1050000, source_pdf: "apex_meridian_2025-03-22_dist1.pdf" },
        { id: "apex-dist2", date: "2025-08-15", type: "distribution", docType: "Distribution #2", amount: 450000, source_pdf: "apex_meridian_2025-08-15_dist2.pdf" },
        // Seeded duplicate of Distribution #2 — the "Remove duplicate" demo. Not a real document.
        { id: "apex-dist2-dup", date: "2025-08-16", type: "distribution", docType: "Distribution #2 (re-sent)", amount: 450000, source_pdf: "apex_meridian_2025-08-15_dist2.pdf", synthetic: true, duplicateOf: "apex-dist2" },
        {
          id: "apex-cas", date: "2025-09-30", type: "nav_snapshot", docType: "Capital Account Statement",
          nav: 3800000, commitment: 5000000, paidIn: 3000000, totalDist: 1500000, unfunded: 2000000,
          source_pdf: "apex_meridian_2025-09-30_cas.pdf",
        },
        { id: "apex-cc4", date: "2025-10-28", type: "capital_call", docType: "Capital Call #4", amount: 750000, source_pdf: "apex_meridian_2025-10-28_cc4.pdf" },
      ],
    },
    {
      id: "fnd_citco_saltwater_credit_opps_ii",
      portal: "Citco",
      fundName: "Saltwater Credit Opportunities Fund II",
      currency: "USD",
      events: [
        {
          id: "citco-qs", date: "2025-09-30", type: "nav_snapshot", docType: "Quarterly Statement",
          nav: 9250000, commitment: 10000000, paidIn: 8000000, totalDist: 2000000, unfunded: 2000000,
          source_pdf: "citco_saltwater_2025-09-30_qs.pdf",
        },
        { id: "citco-cc", date: "2025-11-05", type: "capital_call", docType: "Capital Call Notice", amount: 500000, source_pdf: "citco_saltwater_2025-11-05_cc.pdf" },
      ],
    },
    {
      id: "fnd_moonfare_vintage_2024_coinvest",
      portal: "Moonfare",
      fundName: "Vintage 2024 Co-Investment Pool",
      currency: "EUR",
      events: [
        { id: "moon-sub", date: "2024-06-20", type: "commitment", docType: "Subscription Confirmation", amount: 2000000, source_pdf: "moonfare_v2024_2024-06-20_subscription.pdf" },
        { id: "moon-cc", date: "2024-11-15", type: "capital_call", docType: "Capital Call Notice", amount: 800000, source_pdf: "moonfare_v2024_2024-11-15_cc.pdf" },
        { id: "moon-dist", date: "2025-09-12", type: "distribution", docType: "Distribution Notice", amount: 120000, source_pdf: "moonfare_v2024_2025-09-12_dist.pdf" },
      ],
    },
    {
      id: "fnd_buligo_ira_fund_ii",
      portal: "Buligo",
      fundName: "Buligo IRA Fund II",
      currency: "USD",
      events: [
        { id: "buligo-cc", date: "2024-11-30", type: "capital_call", docType: "Capital Call Notice", amount: 750000, source_pdf: "buligo_ira2_2024-11-30_cc.pdf" },
        {
          id: "buligo-qs", date: "2025-09-30", type: "nav_snapshot", docType: "Quarterly Statement",
          nav: 2000000, commitment: 3000000, paidIn: 3000000, totalDist: 4500000, unfunded: 0,
          source_pdf: "buligo_ira2_2025-09-30_qs.pdf",
        },
      ],
    },
  ],
};

const MEMBER_ID = "mbr_sandbox_holdings_lp";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isLive = (ev) => !ev.rejected;

// Chronological order; on a tie, statements sort LAST so same-day notices fold in first.
const KIND_ORDER = { commitment: 0, capital_call: 1, distribution: 2, nav_snapshot: 3 };
function byLedgerOrder(a, b) {
  if (a.date !== b.date) return a.date.localeCompare(b.date);
  return (KIND_ORDER[a.type] ?? 9) - (KIND_ORDER[b.type] ?? 9);
}

function latestSnapshot(events) {
  return events
    .filter((e) => e.type === "nav_snapshot" && isLive(e))
    .sort(byLedgerOrder)
    .at(-1) || null;
}

function commitmentOf(fund, snap) {
  if (snap?.commitment != null) return snap.commitment;
  const sub = fund.events.find((e) => e.type === "commitment" && isLive(e));
  return sub?.amount ?? null;
}

// ─── Fold: statement-as-checkpoint roll-up (the per-member position) ──────────────

export function foldFund(fund) {
  const events = fund.events.filter(isLive);
  const snap = latestSnapshot(events);

  let called;
  let distributed;
  let nav;
  let navBasis;

  if (snap) {
    const after = (e) => e.date > snap.date;
    const callsAfter = events.filter((e) => e.type === "capital_call" && after(e)).reduce((s, e) => s + e.amount, 0);
    const distsAfter = events.filter((e) => e.type === "distribution" && after(e)).reduce((s, e) => s + e.amount, 0);
    called = snap.paidIn + callsAfter;
    distributed = snap.totalDist + distsAfter;
    nav = snap.nav;
    navBasis = "tvpi";
  } else {
    called = events.filter((e) => e.type === "capital_call").reduce((s, e) => s + e.amount, 0);
    distributed = events.filter((e) => e.type === "distribution").reduce((s, e) => s + e.amount, 0);
    nav = null;
    navBasis = "dpi_only";
  }

  const commitment = commitmentOf(fund, snap);
  const tvpi = nav != null && called > 0 ? (nav + distributed) / called : null;
  const dpi = called > 0 ? distributed / called : null;
  const rvpi = nav != null && called > 0 ? nav / called : null;
  const unfunded = commitment != null ? Math.max(commitment - called, 0) : null;

  return { commitment, called, distributed, nav, navBasis, tvpi, dpi, rvpi, unfunded };
}

// ─── Overview tree (mirrors /v3/ledger/overview) ────────────────────────────────

export function buildOverview(portfolio) {
  const byPortal = new Map();
  for (const fund of portfolio.funds) {
    const fold = foldFund(fund);
    const lastEvent = fund.events.filter(isLive).slice().sort(byLedgerOrder).at(-1);
    const member = {
      position_id: `${fund.id}__${MEMBER_ID}`,
      member_id: MEMBER_ID,
      member_name: portfolio.investor,
      commitment: fold.commitment,
      called: fold.called,
      distributed: fold.distributed,
      unfunded: fold.unfunded,
      latest_nav: fold.nav,
      latest_nav_date: latestSnapshot(fund.events.filter(isLive))?.date || null,
      tvpi: fold.tvpi,
      dpi: fold.dpi,
      rvpi: fold.rvpi,
      moic: fold.tvpi,
      tvpi_basis: fold.navBasis,
      commitment_basis: "extracted",
      last_event_date: lastEvent?.date || null,
      is_fund_level: false,
      discrepancies: [],
    };
    const fundNode = {
      fund_id: fund.id,
      fund_name: fund.fundName,
      currency: fund.currency,
      member_count: 1,
      total_commitment: fold.commitment,
      total_called: fold.called,
      total_distributed: fold.distributed,
      total_unfunded: fold.unfunded,
      total_nav: fold.nav,
      members: [member],
    };
    const portal = fund.portal.toLowerCase();
    if (!byPortal.has(portal)) byPortal.set(portal, []);
    byPortal.get(portal).push(fundNode);
  }
  return { portals: [...byPortal.entries()].map(([portal, funds]) => ({ portal, funds })) };
}

// ─── Document timeline (mirrors /v3/ledger/.../document-timeline) ─────────────────

const EVENT_TYPE_MAP = { commitment: "commitment", capital_call: "capital_call", distribution: "distribution", nav_snapshot: "nav" };
const FIELD_MAP = { commitment: "commitment", capital_call: "paid_in_capital", distribution: "total_distributions", nav_snapshot: "nav" };

function metricsOf(r) {
  const tvpi = r.nav != null && r.called > 0 ? (r.nav + r.distributed) / r.called : null;
  const dpi = r.called > 0 ? r.distributed / r.called : null;
  const rvpi = r.nav != null && r.called > 0 ? r.nav / r.called : null;
  return { commitment: r.commitment, called: r.called, distributed: r.distributed, nav: r.nav, tvpi, dpi, rvpi };
}

const POS_FIELDS = ["commitment", "called", "distributed", "nav", "tvpi", "dpi", "rvpi"];
function deltaOf(prev, pos) {
  if (!prev) return null;
  const d = {};
  for (const f of POS_FIELDS) {
    d[f] = prev[f] == null || pos[f] == null ? null : pos[f] - prev[f];
  }
  return d;
}

export function buildDocTimeline(fund, { includeRejected = false } = {}) {
  const events = fund.events
    .filter((e) => includeRejected || !e.rejected)
    .slice()
    .sort(byLedgerOrder);

  const running = { commitment: null, called: 0, distributed: 0, nav: null };
  let prev = null;
  const documents = [];

  for (const ev of events) {
    if (!ev.rejected) {
      if (ev.type === "commitment") running.commitment = ev.amount;
      else if (ev.type === "capital_call") running.called += ev.amount;
      else if (ev.type === "distribution") running.distributed += ev.amount;
      else if (ev.type === "nav_snapshot") {
        running.called = ev.paidIn;
        running.distributed = ev.totalDist;
        running.nav = ev.nav;
        if (ev.commitment != null) running.commitment = ev.commitment;
      }
    }
    const pos = metricsOf(running);
    const amountCents = Math.round((ev.type === "nav_snapshot" ? ev.nav : ev.amount) * 100);
    documents.push({
      source_doc_id: `documents/${fund.portal.toLowerCase()}/${ev.source_pdf}`,
      original_filename: ev.source_pdf,
      source_doc_type_raw: ev.docType,
      event_date: ev.date,
      status: ev.rejected ? "rejected" : "active",
      discrepancies: ev.synthetic ? ["Possible duplicate of an earlier distribution"] : [],
      contributions: [
        {
          ledger_id: ev.id,
          event_type: EVENT_TYPE_MAP[ev.type] || ev.type,
          amount_cents: amountCents,
          amount_semantic: ev.type === "nav_snapshot" ? "cumulative" : "incremental",
          field_name: FIELD_MAP[ev.type] || "amount",
          status: ev.rejected ? "rejected" : "active",
        },
      ],
      position_after: ev.rejected ? (prev || metricsOf(running)) : pos,
      deltas: ev.rejected ? null : deltaOf(prev, pos),
    });
    if (!ev.rejected) prev = pos;
  }
  return { documents };
}

// ─── currency helper (used by the report builder) ───────────────────────────────

export function currenciesOf(funds) {
  const seen = [];
  for (const f of funds) if (!seen.includes(f.currency)) seen.push(f.currency);
  return seen;
}

// ─── Edit reducer: pure, returns a new portfolio (nothing is persisted) ──────────

export function applyEdit(portfolio, edit) {
  const next = structuredCloneSafe(portfolio);
  const fund = next.funds.find((f) => f.id === edit.fundId);

  switch (edit.kind) {
    case "correct": {
      const ev = fund?.events.find((e) => e.id === edit.noticeId);
      if (ev) {
        if (edit.amount != null) {
          if (ev.type === "nav_snapshot") ev.nav = edit.amount;
          else ev.amount = edit.amount;
        }
        if (edit.date) ev.date = edit.date;
        ev.corrected = true;
      }
      break;
    }
    case "reject": {
      const ev = fund?.events.find((e) => e.id === edit.noticeId);
      if (ev) ev.rejected = true;
      break;
    }
    case "move": {
      // Move every event for this fund's position to another fund (the live
      // "Move to fund" action; the demo has one investor per fund).
      const to = next.funds.find((f) => f.id === edit.toFundId);
      if (fund && to) {
        for (const ev of fund.events) ev.reassigned = true;
        to.events.push(...fund.events);
        fund.events = [];
      }
      break;
    }
    default:
      break;
  }
  return next;
}

function structuredCloneSafe(obj) {
  if (typeof structuredClone === "function") return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
}
